import React, { useEffect } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { getAdminUserDetails } from "store/actions/usersAction"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useTranslation } from "react-i18next"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"

const getAdminUserStatusDisplay = (status, t) => {
  switch (Number(status)) {
    case 1:
      return { label: t("Active"), badgeClass: "bg-success" }
    case 0:
      return { label: t("Inactive"), badgeClass: "bg-warning" }
    case 2:
      return { label: t("Deleted"), badgeClass: "bg-danger" }
    case 3:
      return { label: t("Suspended"), badgeClass: "bg-secondary" }
    case 4:
      return { label: t("Pending"), badgeClass: "bg-info" }
    default:
      return { label: t("Unknown"), badgeClass: "bg-secondary" }
  }
}

const SuperAdminViewUser = () => {
  const { t } = useTranslation()

  const location = useLocation()
  const isDeletedUserContext = location.pathname.includes("/deleted-users/")
  document.title = isDeletedUserContext
    ? t("Deleted Users | Super Admin")
    : t("User Details | Super Admin")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    const permissionId = isDeletedUserContext ? "sa_users_5" : "sa_users_1"
    const isPermissionView = isPermissionEnabled(
      userRole,
      103,
      permissionId,
      accountType,
      accountScope
    )
    if (!isPermissionView) {
      navigateToAccessibleRoute(navigate)
      return
    }
    dispatch(getAdminUserDetails({ id }))
  }, [
    userRole,
    accountType,
    accountScope,
    isSuperAdminSession,
    isDeletedUserContext,
    navigate,
    dispatch,
    id,
  ])

  const { loading, userDetails } = useSelector(state => state.Users)
  if (loading) return <Spinners />

  const statusDisplay = getAdminUserStatusDisplay(userDetails?.status, t)

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl="12">
            <Card className="user_personal_info">
              <CardBody>
                <CardTitle className="mb-4 d-flex align-items-center">
                  {t("Personal Information")}
                </CardTitle>
                <div className="table-responsive">
                  {Object.values(userDetails).length === 0 ? (
                    <p className="text-center">{t("No user found")}</p>
                  ) : (
                    <Table className="table-nowrap mb-0">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{userDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("First Name")}</th>
                          <td>{userDetails.firstName || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Last Name")}</th>
                          <td>{userDetails.lastName || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Phone Number")}</th>
                          <td>{userDetails.phoneNumber || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Username")}</th>
                          <td>{userDetails.username || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Email")}</th>
                          <td>{userDetails.email || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Role")}</th>
                          <td>{userDetails?.role?.name || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            <Badge className={statusDisplay.badgeClass}>
                              {statusDisplay.label}
                            </Badge>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Allow Exports")}</th>
                          <td>{userDetails?.allowExports ? t("Yes") : t("No")}</td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default SuperAdminViewUser
